import { useCallback, useEffect, useState } from "react";
import type { AccountRequest, DishRequest } from "@al-life/types";
import { api } from "@/lib/api";

type Status = "idle" | "loading" | "success" | "error";

/**
 * Admin message-center data: account requests + cooking orders + unread count.
 * Backed by one call to GET /notifications/summary. No polling — `reload()` runs
 * on mount (when enabled), and callers re-run it when the panel opens or after an
 * action. Only fetches when `enabled` (i.e. the caller is an admin).
 */
export function useNotifications(enabled: boolean) {
  const [accountRequests, setAccountRequests] = useState<AccountRequest[]>([]);
  const [cookingRequests, setCookingRequests] = useState<DishRequest[]>([]);
  const [unread, setUnread] = useState(0);
  const [status, setStatus] = useState<Status>("idle");
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!enabled) return;
    setStatus("loading");
    const res = await api.notifications.summary();
    if (res.success && res.data) {
      setAccountRequests(res.data.accountRequests);
      setCookingRequests(res.data.cookingRequests);
      setUnread(res.data.unread);
      setStatus("success");
      setError(null);
    } else {
      setStatus("error");
      setError(res.error ?? "Could not load notifications");
    }
  }, [enabled]);

  useEffect(() => {
    if (enabled) void reload();
  }, [enabled, reload]);

  /** Approve an account request → creates the user, then refresh. */
  const approveAccount = useCallback(
    async (id: string) => {
      const res = await api.auth.requests.approve(id);
      if (res.success) await reload();
      return res;
    },
    [reload]
  );

  /** Dismiss (delete) an account request, then refresh. */
  const dismissAccount = useCallback(
    async (id: string) => {
      const res = await api.auth.requests.dismiss(id);
      if (res.success) await reload();
      return res;
    },
    [reload]
  );

  /** Mark a cooking order done / not-done, then refresh. */
  const setCookingHandled = useCallback(
    async (slug: string, id: string, handled: boolean) => {
      const res = await api.cooking.requests.setHandled(slug, id, handled);
      if (res.success) await reload();
      return res;
    },
    [reload]
  );

  /** Delete a cooking order, then refresh. */
  const removeCooking = useCallback(
    async (slug: string, id: string) => {
      const res = await api.cooking.requests.remove(slug, id);
      if (res.success) await reload();
      return res;
    },
    [reload]
  );

  return {
    accountRequests,
    cookingRequests,
    unread,
    status,
    error,
    reload,
    approveAccount,
    dismissAccount,
    setCookingHandled,
    removeCooking,
  };
}
